/**
 * Facility-owned Local Carousel Ads — canonical doc:
 * `facilities/{facilityId}/localCarousel/main`
 *
 * Denormalized zip listings: `zips/{zip}/listings/lc_{facilityId}` or `lc_{facilityId}_{groupId}`
 * per geographic targeting group (written by Cloud Function).
 */

export const LOCAL_CAROUSEL_SUBCOLLECTION = "localCarousel";
export const LOCAL_CAROUSEL_DOC_ID = "main";

/** Zip listing document id prefix (doc id = lc_{facilityId}). */
export const LOCAL_CAROUSEL_LISTING_ID_PREFIX = "lc_";

export const LISTING_TYPE_LOCAL_CAROUSEL = "local_carousel";

/** Patient-only gender targeting (extend patientSegments later). */
export const LOCAL_CAROUSEL_GENDER_ALL = "all";
export const LOCAL_CAROUSEL_GENDER_MALE = "male";
export const LOCAL_CAROUSEL_GENDER_FEMALE = "female";

export const LOCAL_CAROUSEL_GENDERS = [
  LOCAL_CAROUSEL_GENDER_ALL,
  LOCAL_CAROUSEL_GENDER_MALE,
  LOCAL_CAROUSEL_GENDER_FEMALE,
];

export const LOCAL_CAROUSEL_GENDER_LABELS = {
  [LOCAL_CAROUSEL_GENDER_ALL]: "All patients",
  [LOCAL_CAROUSEL_GENDER_MALE]: "Male patients",
  [LOCAL_CAROUSEL_GENDER_FEMALE]: "Female patients",
};

/**
 * @param {string} facilityId
 * @returns {string}
 */
export function localCarouselZipListingDocId(facilityId) {
  const id = typeof facilityId === "string" ? facilityId.trim() : "";
  return id ? `${LOCAL_CAROUSEL_LISTING_ID_PREFIX}${id}` : "";
}

/**
 * @param {unknown} raw
 * @returns {typeof LOCAL_CAROUSEL_GENDER_ALL | typeof LOCAL_CAROUSEL_GENDER_MALE | typeof LOCAL_CAROUSEL_GENDER_FEMALE}
 */
export function normalizeLocalCarouselGender(raw) {
  if (typeof raw !== "string") {
    return LOCAL_CAROUSEL_GENDER_ALL;
  }
  const g = raw.trim().toLowerCase();
  if (g === LOCAL_CAROUSEL_GENDER_MALE || g === "m") {
    return LOCAL_CAROUSEL_GENDER_MALE;
  }
  if (g === LOCAL_CAROUSEL_GENDER_FEMALE || g === "f") {
    return LOCAL_CAROUSEL_GENDER_FEMALE;
  }
  return LOCAL_CAROUSEL_GENDER_ALL;
}

/**
 * @param {unknown} data
 * @returns {{ zipCodes: string[], locationId: string, facilityDisplayName: string, slides: object[], lastSyncedZips: string[], geographicTargetingGroups: object[] }}
 */
export function normalizeLocalCarouselMainDocument(data) {
  const zipCodes = Array.isArray(data?.zipCodes)
    ? data.zipCodes.map((z) => String(z).trim()).filter(Boolean)
    : [];

  const slides = Array.isArray(data?.slides) ? data.slides : [];
  const geographicTargetingGroups = Array.isArray(data?.geographicTargetingGroups)
    ? data.geographicTargetingGroups
    : [];

  return {
    zipCodes,
    locationId: typeof data?.locationId === "string" ? data.locationId.trim() : "",
    facilityDisplayName:
      typeof data?.facilityDisplayName === "string"
        ? data.facilityDisplayName.trim()
        : "",
    lastSyncedZips: Array.isArray(data?.lastSyncedZips)
      ? data.lastSyncedZips.map((z) => String(z).trim()).filter(Boolean)
      : [],
    geographicTargetingGroups,
    slides: slides.map((s) => ({
      id:
        typeof s?.id === "string" && s.id.trim()
          ? s.id.trim()
          : typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `ls_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      message: typeof s?.message === "string" ? s.message : "",
      buttonLabel:
        typeof s?.buttonLabel === "string" && s.buttonLabel.trim()
          ? s.buttonLabel.trim()
          : "Learn more",
      href: typeof s?.href === "string" ? s.href : "/",
      logoUrl: typeof s?.logoUrl === "string" ? s.logoUrl.trim() : "",
      enabled: s?.enabled !== false,
      patientSegments: {
        gender: normalizeLocalCarouselGender(s?.patientSegments?.gender ?? s?.patientGender),
      },
    })),
  };
}
